import { ReactNode, useRef } from 'react'
import { AriaNumberFieldProps, useLocale } from 'react-aria'
import { useNumberField } from 'react-aria'
import { useNumberFieldState } from 'react-stately'

import { classNames } from '@/modules/shared/utils/classNames'

export interface NumberInputProps extends AriaNumberFieldProps {
  showStepper?: boolean
  className?: string
  loading?: boolean
  rightUnit?: string | ReactNode
  leftUnit?: string | ReactNode
  hasError?: boolean
  errorMessage?: string
  hasWarning?: boolean
  disableArrowKeys?: boolean
}

export default function NumberInput(props: NumberInputProps) {
  const { locale } = useLocale()

  const state = useNumberFieldState({ ...props, locale: locale })

  const inputRef = useRef<HTMLInputElement>(null)

  const { inputProps, errorMessageProps } = useNumberField(props, state, inputRef)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.disableArrowKeys && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      if (props.onKeyDown) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        props.onKeyDown(e as any)
      }
    } else {
      if (inputProps.onKeyDown) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inputProps.onKeyDown(e as any)
      }
    }
  }

  const removeMarginClassName = (classes = '', isReturnMarginClassName = false) => {
    const classNames = classes.split(' ')
    return classNames
      .filter((className) => {
        if (isReturnMarginClassName) {
          return className.match(/(mt|mx|my|mb|mr|ml)-\S+/)
        }
        return !className.match(/(mt|mx|my|mb|mr|ml)-\S+/)
      })
      .join(' ')
  }

  return (
    // Should add margins to the container because leftUnit and rightUnit are absolute so it will always stay at its position even we add margin to the input
    <div className={removeMarginClassName(props.className, true)}>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 top-1/2 flex -translate-y-1/2 items-center pl-3">
          {props.leftUnit && (
            <span className="w-[4.5rem] rounded-md bg-gray-200 px-3 py-1.5 text-center">{props.leftUnit}</span>
          )}
        </div>
        <input
          {...inputProps}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          className={classNames(removeMarginClassName(props.className), {
            'pl-[6rem]': !!props.leftUnit,
            'pr-[6rem]': !!props.rightUnit,
            'border-error focus:border-error focus:outline-none focus:ring-error': props.hasError,
            'border-warning focus:border-warning focus:outline-none focus:ring-warning':
              props.hasWarning && !props.hasError,
            'border-gray-300 focus:border-primary/30 focus:outline-none': !props.hasError && !props.hasWarning,
            'cursor-not-allowed border-gray-300 bg-gray-200': props.isDisabled,
            'cursor-progress bg-gray-200': props.loading,
          })}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 top-1/2 flex -translate-y-1/2 items-center pr-3">
          {props.rightUnit && (
            <span className="w-[4.5rem] rounded-md bg-gray-200 px-3 py-1.5 text-center">{props.rightUnit}</span>
          )}
        </div>
      </div>
      {props.hasError && props.errorMessage && (
        <span {...errorMessageProps} className="mt-1 block text-sm text-error">
          {props.errorMessage}
        </span>
      )}
    </div>
  )
}
